<template>
  <div class="invoice-dialog">
    <v-card class="flex-column full-height" tile :loading="isLoading ? 'secondary' : false">
      <v-toolbar
        style="height: auto;"
        elevation="0"
        class="py-3 toolbar-with-subheader flex-grow-0"
        color="white"
      >
        <div class="toolbar-titles-container overflow-hidden">
          <template v-if="isLoading">
            <v-toolbar-title class="pl-5 mt-1">
              <v-skeleton-loader
                type="heading"
                style="width: 200px; max-width: 200px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
            <v-toolbar-title class="toolbar-subheader pl-5 mt-2">
              <v-skeleton-loader
                type="text"
                style="width: 120px; max-width: 120px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
          </template>
          <template v-else>
            <v-toolbar-title class="pl-5 d-flex">
              {{ title }}
              <copy-to-clipboard-button
                :text="invoice?.invoice_number"
                class="ml-2"
              ></copy-to-clipboard-button>
            </v-toolbar-title>
            <v-toolbar-title class="pl-5 toolbar-subheader">{{ subtitle }}</v-toolbar-title>
          </template>
        </div>
        <v-spacer></v-spacer>
        <print-button
          v-if="!isMobile"
          button-text="Skriv ut fakturan"
          :print-object="invoice?.print"
          icon
        ></print-button>
        <download-button
          v-if="!isMobile"
          button-text="Ladda ner fakturan"
          :url="invoice?.url"
          icon
        ></download-button>
        <v-btn icon v-if="isMobile" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-tabs
        v-model="tabIndex"
        color="secondary"
        show-arrows
        grow
      >
        <v-tab href="#invoice-information">
          Information
        </v-tab>
        <v-tab href="#sales-items">
          Innehåll
        </v-tab>
        <v-tab href="#payments">
          <!-- Fakturahändelser -->
          Betalningar & Krediteringar
        </v-tab>
        <v-tab href="#emails">
          <v-badge
            :value="emailsCount !== ''"
            color="secondary"
            :content="emailsCount"
            offset-x="-2"
            offset-y="8"
          >
            <span>Email</span>
          </v-badge>
        </v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-tabs-items v-model="tabIndex">
        <v-tab-item value="invoice-information">
          <v-sheet class="flex-grow-1">
            <invoice-data-list
              :is-loading="isLoading"
              :invoice="invoice"
              :hidden-sections="hiddenDataListSections"
              customer-clickable
              booking-clickable
              @close="close"
            ></invoice-data-list>
          </v-sheet>
        </v-tab-item>
        <v-tab-item value="sales-items">
          <v-sheet class="flex-grow-1">
            <sales-items-table :sales-items="salesItems"></sales-items-table>
          </v-sheet>
        </v-tab-item>
        <v-tab-item value="payments">
          <v-sheet class="flex-grow-1 overflow-x-hidden">
            <payments-timeline
              :payment-applications="paymentApplications"
              :credit-applications="creditApplications"
              :invoice-id="invoice?.id"
              :allow-crediting="!isCreditInvoice"
              :allow-convert-to-customer-credit="allowConvertToCustomerCredit"
              @start-register-payment="startRegisterPayment"
              @start-register-credit="startRegisterCredit"
            ></payments-timeline>
          </v-sheet>
        </v-tab-item>
        <v-tab-item value="emails">
          <v-sheet
            class="overflow-y-auto pa-3 full-height"
          >
            <email-timeline
              :emails="emails"
              :invoice-id="invoice?.id"
              :payment-ids="invoice?.payment_applications?.map(pa => pa.payment_id) ?? []"
              :is-loading="isLoading"
              no-emails-message="Inga email har skickats"
            >
              <template v-slot:timeline-actions>
                <v-timeline-item
                  fill-dot
                  color="secondary"
                >
                  <template v-slot:icon>
                    <v-icon color="white">mdi-email-fast</v-icon>
                  </template>
                  <div class="d-inline-flex flex-column ga-2">
                    <v-btn
                      v-if="showResendInvoicePlain"
                      @click="resendInvoice(false)"
                      :loading="isSendingInvoice"
                      :disabled="isSendingReminder"
                      :class="{ 'btn-two-rows': isMobile }"
                      color="secondary"
                    >
                      Skicka fakturakopia
                    </v-btn>
                    <v-btn
                      v-if="showResendInvoiceWithLink"
                      @click="resendInvoice(true)"
                      :loading="isSendingInvoice"
                      :disabled="isSendingReminder"
                      :class="{ 'btn-two-rows': isMobile }"
                      color="secondary"
                    >
                      Skicka faktura med betallänk
                    </v-btn>
                    <v-btn
                      v-show="showSendReminderButton"
                      @click="sendReminder"
                      :loading="isSendingReminder"
                      :disabled="isSendingInvoice"
                      :class="{ 'btn-two-rows': isMobile }"
                      color="warning"
                      outlined
                    >
                      Skicka påminnelse
                    </v-btn>
                  </div>
                </v-timeline-item>
              </template>
            </email-timeline>
          </v-sheet>
        </v-tab-item>
      </v-tabs-items>
      <v-card-actions class="mt-auto flex-grow-0 pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          @click="startInvoiceCheckout"
          :disabled="isLoading"
        >
          {{ startPaymentButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- register-payment overlay -->
    <div class="d-flex flex-column overflow-hidden" style="position: absolute; height: 100%; width: 100%; top: 0; left: 0; pointer-events: none; z-index: 2;">
      <v-fade-transition>
        <div v-show="isShowRegisterPaymentOverlay" @click="closeRegisterPaymentOverlay" style="height: 76px; opacity: 0.23; background-color: rgb(33, 33, 33); pointer-events: auto;"></div>
      </v-fade-transition>
      <v-scroll-y-reverse-transition>
        <v-card
          v-show="isShowRegisterPaymentOverlay"
          class="flex-grow-1"
          light
          style="pointer-events: auto; margin-top: -12px;"
        >
          <v-toolbar class="flex-shrink-1 flex-grow-0" flat>
            <div class="d-flex">
              <v-toolbar-title class="headline">{{ registerPaymentTitle }}</v-toolbar-title>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeRegisterPaymentOverlay">
              <v-icon>
                mdi-close
              </v-icon>
            </v-btn>
          </v-toolbar>
          <div class="flex-grow-1 pt-3" style="height: calc(100% - 64px);">
            <register-payment-overlay
              :is-open="isShowRegisterPaymentOverlay"
              :invoice="invoice"
              @close="closeRegisterPaymentOverlay"
            ></register-payment-overlay>
          </div>
        </v-card>
      </v-scroll-y-reverse-transition>
    </div>

    <!-- invoice-checkout overlay -->
    <div class="d-flex flex-column overflow-hidden" style="position: absolute; height: 100%; width: 100%; top: 0; left: 0; pointer-events: none; z-index: 2;">
      <v-fade-transition>
        <div v-show="isShowInvoiceCheckoutOverlay" @click="closeInvoiceCheckoutOverlay" style="height: 76px; opacity: 0.23; background-color: rgb(33, 33, 33); pointer-events: auto;"></div>
      </v-fade-transition>
      <v-scroll-y-reverse-transition>
        <v-card
          v-show="isShowInvoiceCheckoutOverlay"
          class="flex-grow-1"
          light
          style="pointer-events: auto; margin-top: -12px;"
        >
          <v-toolbar class="flex-shrink-1 flex-grow-0" flat>
            <div class="d-flex">
              <v-toolbar-title class="headline">{{ invoiceCheckoutTitle }}</v-toolbar-title>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeInvoiceCheckoutOverlay">
              <v-icon>
                mdi-close
              </v-icon>
            </v-btn>
          </v-toolbar>
          <div class="flex-grow-1 pt-3" style="height: calc(100% - 64px);">
            <invoice-checkout-overlay
              :is-open="isShowInvoiceCheckoutOverlay"
              :invoice="invoice"
              @close="closeInvoiceCheckoutOverlay"
            ></invoice-checkout-overlay>
          </div>
        </v-card>
      </v-scroll-y-reverse-transition>
    </div>

    <!-- ota-virtual-card overlay -->
    <div class="d-flex flex-column overflow-hidden" style="position: absolute; height: 100%; width: 100%; top: 0; left: 0; pointer-events: none; z-index: 2;">
      <v-fade-transition>
        <div v-show="isShowOtaVirtualCardOverlay" @click="closeOtaVirtualCardOverlay" style="height: 76px; opacity: 0.23; background-color: rgb(33, 33, 33); pointer-events: auto;"></div>
      </v-fade-transition>
      <v-scroll-y-reverse-transition>
        <v-card
          v-show="isShowOtaVirtualCardOverlay"
          class="flex-grow-1"
          light
          style="pointer-events: auto; margin-top: -12px;"
        >
          <v-toolbar class="flex-shrink-1 flex-grow-0" flat>
            <div class="d-flex">
              <v-toolbar-title class="headline">Debitera Virtuellt Kort</v-toolbar-title>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeOtaVirtualCardOverlay">
              <v-icon>
                mdi-close
              </v-icon>
            </v-btn>
          </v-toolbar>
          <div class="flex-grow-1 pa-6" style="height: calc(100% - 64px); overflow-y: auto;">
            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined class="pa-4 full-height">
                  <div class="d-flex flex-column full-height">
                    <div class="d-flex align-center mb-4">
                      <v-icon color="primary" class="mr-3">mdi-credit-card-search</v-icon>
                      <span class="text-subtitle-1 font-weight-medium">Kortuppgifter</span>
                    </div>
                    <template v-if="hasBookVisitBookingCode">
                      <p class="text-body-2 mb-4">
                        Öppna bokningen hos BookVisit för att se kortuppgifterna tillhörande bokningen
                      </p>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        :href="bookVisitReservationUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <v-icon left>mdi-open-in-new</v-icon>
                        Till BookVisit
                      </v-btn>
                    </template>
                    <template v-else>
                      <v-alert
                        type="warning"
                        text
                        dense
                        class="mb-0"
                      >
                        Denna faktura är inte länkad till någon bokning. Hämta kortuppgifter manuellt.
                      </v-alert>
                    </template>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card outlined class="pa-4 full-height">
                  <div class="d-flex flex-column full-height">
                    <div class="d-flex align-center mb-4">
                      <v-icon color="success" class="mr-3">mdi-credit-card-check</v-icon>
                      <span class="text-subtitle-1 font-weight-medium">Genomför betalning</span>
                    </div>
                    <p class="text-body-2 mb-4">
                      Ange kortuppgifterna direkt på Surfboard Payments säkra betalningssida
                    </p>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="success"
                      :loading="isGeneratingPaymentLink"
                      @click="openVirtualCardPaymentPage"
                    >
                      <v-icon left>mdi-open-in-new</v-icon>
                      Till betalningssida
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <v-alert
              type="info"
              text
              class="mt-6 mb-0"
            >
              <v-icon slot="prepend" color="info">mdi-information</v-icon>
              När betalningen har genomförts hos Surfboard Payments kommer denna faktura automatiskt att markeras som betald. Du behöver inte stanna på denna sida.
            </v-alert>
          </div>
        </v-card>
      </v-scroll-y-reverse-transition>
    </div>

    <!-- register-credit dialog -->
    <v-dialog
      v-model="isShowRegisterCreditDialog"
      :width="isMobile ? '' : '800px'"
      :fullscreen="isMobile"
    >
      <v-card
        v-show="isShowRegisterCreditDialog"
      >
        <v-toolbar class="flex-shrink-1 flex-grow-0" flat>
          <div class="d-flex">
            <v-toolbar-title class="headline">Kreditera Faktura</v-toolbar-title>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  class="ml-2"
                  color="info"
                >mdi-information</v-icon>
              </template>
              <div style="max-width: 400px;">
                <p>Välj vilka rader på fakturan som du vill kreditera.</p>
                <p>Vill du bara kreditera en del av en rad kan du justera antalet eller priset på raden genom att klicka på pennan på den raden.</p>
                <p>Markera "Återför till varukorgen" om du vill att produkten ska läggas tillbaka i bokningens varukorg efter den krediterats (endast tillgängligt om fakturan tillhör en bokning).</p>
              </div>
            </v-tooltip>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeRegisterCreditDialog">
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </v-toolbar>
        <div class="flex-grow-1" style="height: calc(100% - 64px);">
          <register-credit
            :is-open="isShowRegisterCreditDialog"
            :original-invoice="invoice"
            @close="closeRegisterCreditDialog"
          ></register-credit>
        </div>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapState } from 'vuex'

import InvoiceDataList from '@/components/invoice/DataList.vue'
import PaymentsTimeline from '@/components/invoice/PaymentsTimeline.vue'
import RegisterPaymentOverlay from '@/components/admin/RegisterPayment.vue'
import RegisterCredit from '@/components/invoice/RegisterCredit.vue'
import InvoiceCheckoutOverlay from '@/components/invoice/Checkout.vue'
import SalesItemsTable from '@/components/product/SalesItemsTable.vue'
import DownloadButton from '@/components/common/DownloadButton.vue'
import PrintButton from '@/components/common/PrintButton.vue'
import EmailTimeline from '@/components/email/Timeline.vue'
import InvoiceService from '@/services/InvoiceService.js'
import CopyToClipboardButton from '@/components/common/CopyToClipboardButton.vue'

export default {
  name: 'InvoiceDialogContent',
  components: {
    InvoiceDataList,
    PaymentsTimeline,
    RegisterPaymentOverlay,
    RegisterCredit,
    InvoiceCheckoutOverlay,
    SalesItemsTable,
    DownloadButton,
    PrintButton,
    CopyToClipboardButton,
    EmailTimeline
  },
  props: {
    invoice: Object,
    isLoading: Boolean,
    show: {
      type: Boolean,
      default: true
    },
    openTab: {
      type: String,
      default: null
    }
  },
  data: () => ({
    tabIndex: 'invoice-information',
    isShowRegisterPaymentOverlay: false,
    isShowInvoiceCheckoutOverlay: false,
    isShowRegisterCreditDialog: false,
    isShowOtaVirtualCardOverlay: false,
    isGeneratingPaymentLink: false
  }),
  computed: {
    ...mapState(
      {
        isSendingInvoiceIds: state => state.invoice.isSendingInvoiceIds,
        isSendingReminderInvoiceIds: state => state.invoice.isSendingReminderInvoiceIds,
        isElectron: state => state.isElectron
      }
    ),
    isMobile: function () {
      return this.$vuetify.breakpoint.smAndDown
    },
    invoiceType: function () {
      return this.invoice?.invoice_type
    },
    isCreditInvoice: function () {
      return this.invoiceType === 'credit_invoice'
    },
    allowConvertToCustomerCredit: function () {
      return this.isCreditInvoice && this.invoice.remaining_amount < 0
    },
    title: function () {
      if (!this.invoice) {
        return ''
      }
      if (this.isCreditInvoice) {
        return `Kreditfaktura ${this.invoice.invoice_number}`
      } else {
        // fakturor eller kontantfaktura
        return `Faktura ${this.invoice.invoice_number}`
      }
    },
    subtitle: function () {
      if (!this.invoice) {
        return ''
      }
      const formattedDate = this.invoice.invoice_date.format('YYYY-MM-DD')
      return `Fakturadatum ${formattedDate}`
    },
    isOtaCustomer: function () {
      return this.invoice && this.invoice.customer && this.invoice.customer.is_ota_customer
    },
    hasBookVisitBookingCode: function () {
      return this.invoice && this.invoice.bookvisit_booking_code != null
    },
    bookVisitReservationUrl: function () {
      if (!this.hasBookVisitBookingCode) {
        return ''
      }
      return `https://admin.bookvisit.com/Reservation/managereservation?BookingNumberOrCustomerName=${this.invoice.bookvisit_booking_code}`
    },
    surfboardPaymentUrl: function () {
      if (!this.invoice || !this.invoice.surfboard_payment_url) {
        return ''
      }
      return this.invoice.surfboard_payment_url
    },
    startPaymentButtonText: function () {
      if (this.isCreditInvoice) {
        return 'Påbörja återbetalning'
      } else if (this.isOtaCustomer) {
        return 'Debitera Virtuellt Kort'
      } else {
        return 'Ta betalt'
      }
    },
    salesItems: function () {
      return this.invoice?.sales_items ?? []
    },
    paymentApplications: function () {
      return this.invoice?.payment_applications ?? []
    },
    creditApplications: function () {
      return this.invoice?.credit_applications ?? []
    },
    registerPaymentTitle: function () {
      if (this.isCreditInvoice) {
        return 'Registrera en utbetalning'
      }
      return 'Registrera en inbetalning'
    },
    invoiceCheckoutTitle: function () {
      if (this.isCreditInvoice) {
        return 'Genomför en återbetalning'
      }
      return 'Ta betalt'
    },
    hiddenDataListSections: function () {
      if (this.isCreditInvoice) {
        return ['due-date', 'payment-term', 'ocr']
      }
      if (this.invoiceType === 'payment_link') {
        return ['ocr']
      }
      if (this.invoiceType === 'cash_invoice') {
        return ['due-date', 'payment-term', 'ocr']
      }
      return []
    },
    emails: function () {
      return this.invoice?.emails ?? []
    },
    emailsCount: function () {
      if (!this.emails || this.emails.length === 0) {
        return ''
      }
      if (this.emails.length > 99) {
        return '99+'
      }
      return this.emails.length
    },
    showSendReminderButton: function () {
      if (!this.invoice || this.isCreditInvoice) {
        return false
      }
      return this.invoice.isOverdue || this.invoice.isOverdueDeposit
    },
    isSendingInvoice: function () {
      return this.invoice && this.isSendingInvoiceIds.includes(this.invoice.id)
    },
    isSendingReminder: function () {
      return this.invoice && this.isSendingReminderInvoiceIds.includes(this.invoice.id)
    },
    hasSurfboardPaymentOption: function () {
      return this.invoice && Array.isArray(this.invoice.payment_options) && this.invoice.payment_options.includes('surfboard')
    },
    showResendInvoicePlain: function () {
      if (!this.invoice) {
        return false
      }
      if (this.hasSurfboardPaymentOption && this.invoice.status !== 'closed') {
        return false // Om fakturan tidigare skickats som betallänk, och den inte redan är betald, visa inte alternativet att skicka om fakturan utan betallänk
      }
      return true
    },
    showResendInvoiceWithLink: function () {
      if (!this.invoice) {
        return false
      }
      if (this.invoice.status === 'closed') {
        return false // Kan inte skicka betallänk på en stängd faktura
      }
      return true
    }
  },
  methods: {
    close: function () {
      this.$emit('close')
    },
    startRegisterPayment: function () {
      this.isShowRegisterPaymentOverlay = true
    },
    startRegisterCredit: function () {
      this.isShowRegisterCreditDialog = true
    },
    closeRegisterPaymentOverlay: function () {
      this.isShowRegisterPaymentOverlay = false
    },
    closeRegisterCreditDialog: function () {
      this.isShowRegisterCreditDialog = false
    },
    startInvoiceCheckout: function () {
      if (this.isOtaCustomer) {
        this.isShowOtaVirtualCardOverlay = true
      } else {
        this.isShowInvoiceCheckoutOverlay = true
      }
    },
    closeInvoiceCheckoutOverlay: function () {
      this.isShowInvoiceCheckoutOverlay = false
    },
    closeOtaVirtualCardOverlay: function () {
      this.isShowOtaVirtualCardOverlay = false
    },
    openVirtualCardPaymentPage: function () {
      this.isGeneratingPaymentLink = true

      // Öppna fönster synkront för att undvika Safari popup-blockerare
      let newWindow = null
      if (!this.isElectron) {
        newWindow = window.open('', '_blank')
      }

      InvoiceService.getVirtualCardRedemptionLink({ invoiceId: this.invoice.id })
        .then(({ data }) => {
          if (data.status === 'success') {
            const paymentLink = data.data?.payment_link
            if (paymentLink) {
              if (this.isElectron) {
                window.shell.openExternalLink(paymentLink)
              } else {
                newWindow.location.href = paymentLink
              }
            } else if (newWindow) {
              newWindow.close()
            }
          } else if (newWindow) {
            newWindow.close()
          }
        })
        .catch(() => {
          if (newWindow) {
            newWindow.close()
          }
        })
        .finally(() => {
          this.isGeneratingPaymentLink = false
        })
    },
    resendInvoice: function (withPaymentLink = false) {
      this.$store.dispatch('invoice/resendInvoice', { invoiceId: this.invoice.id, withPaymentLink })
    },
    sendReminder: function () {
      this.$store.commit('SET_OPEN_CONFIRMATION_DIALOG', {
        header: 'Skicka betalningspåminnelse',
        body: 'Vill du skicka en betalningspåminnelse till kunden?',
        confirmationFunction: 'invoice/sendInvoiceReminder',
        confirmationArgument: { invoiceId: this.invoice.id },
        withTimer: false
      })
    }
  },
  watch: {
    show: function (newVal) {
      if (newVal) {
        this.tabIndex = this.openTab || 'invoice-information'
      } else {
        this.tabIndex = 'invoice-information'
        this.isShowRegisterPaymentOverlay = false
        this.isShowInvoiceCheckoutOverlay = false
        this.isShowRegisterCreditDialog = false
        this.isShowOtaVirtualCardOverlay = false
      }
    }
  }
}
</script>
